<template>
    <PushCart
      :e-sim-q-r-information="eSimQRInformation"
      :e-sim-q-r-information-loaded="eSimQRInformationLoaded"
      :e-sim-status="eSimStatus"
      :e-sim-status-loaded="eSimStatusLoaded"
      @performESimPush="performESimPush"
      @performESimDeassign="performESimPushDelete"
      ref="pushcart"
    >
      <template v-for="slot in possibleChildSlotsFor('Cart')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </PushCart>
</template>
<script>

import { wrapQuery } from '@/helper/wrapGQLQuery'
import gql from 'graphql-tag'
import useFeature from '@/mixins/useFeature'

import validationFlow from '@/mixins/validators/validationFlow'
import { validationMixin } from 'vuelidate'
import { splitError } from '@/helper/splitError'
import PushCart from '@/components/process/generic/ESimPush/PushCart.vue'
import transaction from '@/mixins/transaction'

export default {
  name: 'ESimPush',
  mixins: [
    validationFlow,
    validationMixin,
    useFeature('esimPush'),

    transaction('eSimPush')
  ],
  components: {
    PushCart
  },
  data: function () {
    return {
      eSimQRInformation: null,
      eSimStatus: null,
      eSimQRInformationLoaded: false,
      eSimStatusLoaded: false
    }
  },
  mounted () {
    this.startLoader()
  },
  methods: {
    performESimPush (eID) {
      this.startLoader()
      const graphQlClient = this.$apolloProvider.defaultClient

      graphQlClient
        .mutate({
          mutation: gql`
        mutation eSimAssign($eSimAssignInput: ESimAssignInput!) {
          eSimAssign(eSimAssignInput: $eSimAssignInput) {
            success
          }
        }
      `,
          variables: {
            eSimAssignInput: {
              eid: eID
            }
          }
        })
        .then(async () => {
          // Refetch Status to update view
          await this.$apollo.queries.eSimStatus.refetch()
          if (!this.eSimDownloadInProgress) {
            this.successToast(
              this.getSlotText(
                'txtPushSuccessToast',
                'Die Push-Benachrichtigung wurde erfolgreich übertragen. Folge nun den Einrichtungsschritten deines Geräts.'
              )
            )
          }
          const data = {
            micro_conversion_category: 'confirm push esim',
            micro_conversion_value: 'esim activation page'
          }
          if (typeof window.trackingNS?.track?.tracking === 'function') {
            window.trackingNS.track.tracking('micro conversion', data)
          }
        })
        .catch((error) => {
          this.errorToast(this.errorMessage(error))
        })
        .finally(() => {
          this.stopLoader()
        })
    },
    performESimPushDelete () {
      this.startLoader()
      const graphQlClient = this.$apolloProvider.defaultClient

      graphQlClient
        .mutate({
          mutation: gql`
        mutation eSimUnassign {
          eSimUnassign {
            success
          }
        }
      `
        })
        .then(async () => {
          // Refetch Status to update view
          await this.$apollo.queries.eSimStatus.refetch()
          if (!this.eSimDownloadInProgress) {
            this.successToast(
              this.getSlotText(
                'txtPushDeleteSuccessToast',
                'Die Einrichtung per Push-Nachricht wurde abgebrochen'
              )
            )
          }
          const data = {
            micro_conversion_category: 'confirm remove esim',
            micro_conversion_value: 'esim activation page'
          }
          if (typeof window.trackingNS?.track?.tracking === 'function') {
            window.trackingNS.track.tracking('micro conversion', data)
          }
        })
        .catch((error) => {
          this.errorToast(this.errorMessage(error))
        })
        .finally(() => {
          this.stopLoader()
        })
    },
    errorMessage (error) {
      const firstMessage = splitError(error)?.[0] || error
      if (firstMessage && firstMessage.extensions?.category === 'graphql') {
        // technical error message, do not use for customer
        return this.fallbackErrorMessage
      }
      return firstMessage.message
    },
    checkAndDisableLoader () {
      if (this.eSimQRInformationLoaded && this.eSimStatusLoaded) {
        this.stopLoader()
      }
    }
  },
  computed: {
    eSimDownloadInProgress: function () {
      return ['DOWNLOAD_IN_PROGRESS', 'IN_PROGRESS'].includes(this.eSimStatus?.eSimStatus)
    },
    possibleActions: function () {
      return this.eSimStatus?.possibleActions && this.eSimStatus?.possibleActions.length > 0 ? this.eSimStatus.possibleActions : []
    },
    fallbackErrorMessage: function () {
      return this.getSlotText('txtDefaultError', 'Leider ist gerade ein Fehler aufgetreten. Bitte probier\'s gleich noch einmal oder melde dich telefonisch.')
    },
    eSimUnavailable: function () {
      return this.eSimStatus?.eSimStatus === 'UNAVAILABLE' || this.eSimStatus?.eSimStatus === 'UNAVAILABLE '
    }
  },
  props: {
    esimUnavailableTargetUrl: {
      type: String,
      default: '/mein-simon/sim'
    }
  },
  apollo: {
    eSimQRInformation: function () {
      return wrapQuery(gql`query eSimQRInformation {
        eSimQRInformation (size: 143) {
          code {
            activationCode
            confirmationCode
            ... on ESimQR {
              qrCode
            }
          }
          smdpPlus
        }
      }`, this, ['eSimQRInformation'], {}, () => {
        if (!this.$apollo.queries.eSimQRInformation.loading) {
          this.eSimQRInformationLoaded = true
          this.checkAndDisableLoader()
        }
      },
      {},
      (error) => {
        this.eSimQRInformationLoaded = true
        this.checkAndDisableLoader()
        splitError(error).forEach(e => {
          this.error = e.message
        })
        const data = {
          type: 'technical',
          message: this.error
        }
        if (typeof window.trackingNS?.track?.tracking === 'function') {
          window.trackingNS.track.tracking('error', data)
        }
      })
    },
    eSimStatus: function () {
      return wrapQuery(gql`query eSimStatus {
        eSimStatus {
          eid
          eSimStatus
          possibleActions
        }
      }`, this, ['eSimStatus'], {}, () => {
        if (!this.$apollo.queries.eSimStatus.loading) {
          this.eSimStatusLoaded = true
          if (this.eSimUnavailable) {
            window.location.href = this.esimUnavailableTargetUrl
          }
          if (this.possibleActions.includes('PUSH_DEASSIGN')) {
            this.$refs.pushcart.openDeleteModal()
            this.pushCollapseVisible = true
            setTimeout(() => { document.getElementById('accordion-push').scrollIntoView({ behavior: 'smooth' }) }, 100)
          }
          this.checkAndDisableLoader()
        }
      },
      {},
      (error) => {
        this.eSimStatusLoaded = true
        this.eSimStatus = { eSimStatus: 'INSTALLED' }
        this.checkAndDisableLoader()
        splitError(error).forEach(e => {
          this.error = e.message
        })
        const data = {
          type: 'technical',
          message: this.error
        }
        if (typeof window.trackingNS?.track?.tracking === 'function') {
          window.trackingNS.track.tracking('error', data)
        }
      })
    }
  }
}
</script>
